/**
 * SaveSlotService handles save slot switching based on auth state.
 *
 * Save slots:
 * - Anonymous: roadTripSave_anonymous (guest progress, NEVER synced)
 * - Account: roadTripSave_account (logged-in user, synced with cloud)
 *
 * CRITICAL SEPARATION:
 * - These slots are COMPLETELY INDEPENDENT
 * - Anonymous slot is NEVER auto-promoted to account
 * - Account data comes from CLOUD SYNC, not from anonymous slot
 * - Sign out switches to anonymous slot (preserving guest progress)
 *
 * This prevents data loss when signing into an existing account.
 */
export class SaveSlotService {
    constructor(saveManager, accountManager) {
        this.saveManager = saveManager;
        this.accountManager = accountManager;
        this._initialized = false;
    }

    /**
     * Initialize save slots based on current auth state.
     * Call once during app startup.
     * NOTE: This only does migration. Slot selection is handled by handleAuthStateChange
     * which is called by the auth state listener and runs at the right time.
     * @returns {object|null} The loaded state from the appropriate slot
     */
    async initialize() {
        await this.accountManager.waitForReady();
        this._migrateLegacySaveIfNeeded();
        // NOTE: Do NOT call _selectSlotForCurrentAuth() here!
        // Slot selection is handled by handleAuthStateChange() which is triggered
        // by the auth state listener. Calling it here causes a race condition where
        // we override the correct slot selection made by handleAuthStateChange().
        this._initialized = true;
        console.log('[SaveSlotService] Initialized (migration only, slot set by auth listener)');
        return this.saveManager.load();
    }

    /**
     * Handle auth state change - switch slots appropriately.
     *
     * IMPORTANT: This method ONLY switches slots. It does NOT copy/promote data.
     * - Anonymous users → anonymous slot
     * - Linked users → account slot (cloud sync loads data separately)
     *
     * @param {object} authState - The auth state from AccountManager
     * @returns {{state: object|null, action: string}} Result with action type
     */
    handleAuthStateChange(authState) {
        const isLinked = authState.user && !authState.isAnonymous;

        // Update display name
        this._updateDriverDisplayName(authState);

        if (!isLinked) {
            // Anonymous user - use anonymous slot
            console.log('[SaveSlotService] Auth: anonymous → switching to anonymous slot');
            this.saveManager.switchToAnonymous();
            return {
                state: this.saveManager.load(),
                action: 'switched_to_anonymous'
            };
        }

        // Linked user - switch to account slot
        // DO NOT auto-promote anonymous data - cloud sync handles account data
        console.log('[SaveSlotService] Auth: linked → switching to account slot');
        this.saveManager.switchToAccount();

        // Return whatever is in the account slot (may be null if first time on this device)
        // Cloud sync will load from cloud and populate this slot
        return {
            state: this.saveManager.load(),
            action: 'switched_to_account'
        };
    }

    /**
     * Handle sign out - switch back to anonymous slot
     * @returns {object|null} The anonymous save state (if any)
     */
    handleSignOut() {
        this.saveManager.switchToAnonymous();
        this._updateDriverDisplayName({ user: null, isAnonymous: true });
        return this.saveManager.load();
    }

    /**
     * Check if any save exists (for "NEW GAME" button logic)
     * @returns {boolean}
     */
    hasAnySave() {
        return this.saveManager.hasLegacySave() ||
               this.saveManager.hasAnonymousSave() ||
               this.saveManager.hasAccountSave();
    }

    // ===== Slot Tracking (for startup synchronization) =====

    /**
     * Record which slot was last active.
     * Called when switching slots so startup can determine correct slot
     * before async auth completes.
     * @param {string} slot - 'account' or 'anonymous'
     */
    setLastActiveSlot(slot) {
        localStorage.setItem('idlemile_last_active_slot', slot);
        console.log('[SaveSlotService] Recorded last active slot:', slot);
    }

    /**
     * Get the last active slot marker.
     * @returns {string|null} 'account', 'anonymous', or null if never set
     */
    getLastActiveSlot() {
        return localStorage.getItem('idlemile_last_active_slot');
    }

    /**
     * Determine the intended slot synchronously based on cached markers.
     * Used during startup to select correct slot BEFORE async auth resolves.
     *
     * Logic:
     * 1. If account slot has data AND was last active → use account
     * 2. Otherwise → use anonymous
     *
     * This handles the common case of a logged-in user refreshing the page:
     * their account slot has data and was last active, so we use it immediately
     * rather than loading anonymous first and switching later.
     *
     * @returns {string} 'roadTripSave_account' or 'roadTripSave_anonymous'
     */
    getIntendedSlotSync() {
        const accountSlotHasData = this.saveManager.hasAccountSave();
        const lastActiveSlot = this.getLastActiveSlot();

        // If account slot has data AND was the last active slot, use it
        // This prevents loading anonymous slot first for logged-in users
        if (accountSlotHasData && lastActiveSlot === 'account') {
            console.log('[SaveSlotService] Sync slot check: account (was last active with data)');
            return 'roadTripSave_account';
        }

        console.log('[SaveSlotService] Sync slot check: anonymous (default)');
        return 'roadTripSave_anonymous';
    }

    /**
     * Select slot based on auth state and update last active marker.
     * This is the primary method for slot selection during startup.
     * @param {boolean} isLinked - Whether user is logged in
     */
    selectSlotForAuth(isLinked) {
        if (isLinked) {
            this.saveManager.switchToAccount();
            this.setLastActiveSlot('account');
            console.log('[SaveSlotService] Selected account slot for linked user');
        } else {
            this.saveManager.switchToAnonymous();
            this.setLastActiveSlot('anonymous');
            console.log('[SaveSlotService] Selected anonymous slot');
        }
    }

    // ===== Private Methods =====

    /**
     * Migrate legacy save to appropriate slot (one-time operation)
     */
    _migrateLegacySaveIfNeeded() {
        if (!this.saveManager.hasLegacySave()) return;

        const legacyData = this.saveManager.getLegacySave();
        if (!legacyData) return;

        const isLinked = this.accountManager.isLinked();
        const hasCloudOwner = legacyData._cloudSaveOwnerId;

        if (isLinked && hasCloudOwner) {
            // Legacy was an account save
            localStorage.setItem('roadTripSave_account', JSON.stringify(legacyData));
            console.log('[SaveSlotService] Migrated legacy save to account slot');
        } else {
            // Legacy was anonymous
            localStorage.setItem('roadTripSave_anonymous', JSON.stringify(legacyData));
            console.log('[SaveSlotService] Migrated legacy save to anonymous slot');
        }

        this.saveManager.clearLegacySave();
    }

    /**
     * Select the correct save slot based on current auth state
     */
    _selectSlotForCurrentAuth() {
        if (this.accountManager.isLinked()) {
            this.saveManager.switchToAccount();
            console.log('[SaveSlotService] Selected account slot');
        } else {
            this.saveManager.switchToAnonymous();
            console.log('[SaveSlotService] Selected anonymous slot');
        }
    }

    /**
     * Update the driver display name in the UI
     * @param {object} authState - The auth state from AccountManager
     */
    _updateDriverDisplayName(authState) {
        const el = document.getElementById('opd-driver-name');
        if (!el) return;

        if (!authState.user || authState.isAnonymous) {
            el.textContent = 'ANONYMOUS DRIVER';
            el.classList.remove('linked');
            el.classList.add('anonymous');
        } else {
            const name = authState.user.displayName ||
                        authState.user.email?.split('@')[0] ||
                        'LINKED DRIVER';
            el.textContent = name.toUpperCase();
            el.classList.add('linked');
            el.classList.remove('anonymous');
        }
    }
}
